import { useState } from 'react';
import type { Product } from '@/mocks/products';
import InquiryModal from '@/pages/home/components/InquiryModal';
import { useAuth, applyMargin, calcPartnerRebate } from '@/hooks/useAuth';
import LoginModal from '@/components/feature/LoginModal';
import AtopalmBrandSection from '@/pages/product/components/AtopalmBrandSection';
import DressroomBrandSection from '@/pages/product/components/DressroomBrandSection';
import HBAFBrandSection from '@/pages/product/components/HBAFBrandSection';
import RavielBrandSection from '@/pages/product/components/RavielBrandSection';

interface ProductDetailContentProps {
  product: Product;
}

const badgeColors: Record<string, string> = {
  '추천': 'bg-black text-white',
  '최저가': 'bg-red-600 text-white',
  '인기': 'bg-gray-800 text-white',
  '프리미엄': 'bg-amber-700 text-white',
};

// 역할별 커미션 계산
// admin: 기본 리베이트 그대로, partner: 기본에서 5% 차감 (단, 20% 이하면 그대로)
function getCommission(baseCommission: number, role: 'admin' | 'partner' | null): number {
  if (!role || role === 'admin') return baseCommission;
  // partner: calcPartnerRebate 사용 (25% → 20%, 20% 이하는 그대로)
  return calcPartnerRebate(baseCommission);
}

export default function ProductDetailContent({ product }: ProductDetailContentProps) {
  const [imgError, setImgError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const { isLoggedIn, role } = useAuth();

  const badgeClass = product.badge ? (badgeColors[product.badge] || 'bg-black text-white') : '';

  const categoryZhMap: Record<string, string> = {
    '탕류':'汤类','해조류':'海藻类','음료·두유':'饮料·豆乳','차류':'茶类',
    '스낵·김':'零食·海苔','생활용품':'生活用品','김치·반찬':'泡菜·小菜',
    '스킨케어·뷰티':'护肤·美妆','섬유향수':'衣物香水','핸드크림':'护手霜',
    '운동화':'运动鞋','건강용품':'健康用品'
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
        {/* Left: Image */}
        <div className="relative">
          <div className="relative overflow-hidden rounded-2xl bg-gray-50 aspect-square">
            {!imgError && product.image ? (
              <img
                src={product.image}
                alt={product.nameZh}
                onError={() => setImgError(true)}
                className="w-full h-full object-contain object-center p-4"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <i className="ri-image-add-line text-6xl text-gray-300"></i>
                <span className="text-sm text-gray-400 tracking-wide">图片准备中 / 사진 준비중</span>
              </div>
            )}
            {product.badge && (
              <span className={`absolute top-3 left-3 sm:top-4 sm:left-4 text-xs sm:text-sm font-bold px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-lg tracking-wide ${badgeClass}`}>
                {product.badgeZh}
                <span className="opacity-70 ml-1 sm:ml-1.5 font-normal">/ {product.badge}</span>
              </span>
            )}
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-3 sm:mt-4">
            {product.tags.includes('recommended') && (
              <span className="text-[11px] sm:text-[12px] border border-gray-300 text-gray-600 px-2.5 py-1 rounded-full tracking-wide whitespace-nowrap">
                推荐商品 <span className="text-gray-400">/ 추천 상품</span>
              </span>
            )}
            {product.tags.includes('lowest') && (
              <span className="text-[11px] sm:text-[12px] border border-red-200 text-red-600 px-2.5 py-1 rounded-full tracking-wide whitespace-nowrap">
                最低价 <span className="text-red-400">/ 최저가</span>
              </span>
            )}
            {product.tags.includes('popular') && (
              <span className="text-[11px] sm:text-[12px] border border-gray-300 text-gray-600 px-2.5 py-1 rounded-full tracking-wide whitespace-nowrap">
                热销商品 <span className="text-gray-400">/ 인기 상품</span>
              </span>
            )}
          </div>
        </div>

        {/* Right: Info */}
        <div className="flex flex-col">
          {/* Category & Code */}
          <div className="flex items-center gap-2 mb-3 sm:mb-4 flex-wrap">
            <span className="text-[11px] sm:text-[12px] font-semibold px-2.5 py-1 rounded-full tracking-wide" style={{ backgroundColor: '#F0EAE0', color: '#5C4F42' }}>
              {categoryZhMap[product.category]} <span style={{ color: '#8C7D6E' }}>/ {product.category}</span>
            </span>
            <span className="text-[11px] sm:text-[12px] text-gray-400 font-mono tracking-widest">{product.code}</span>
          </div>

          {/* Brand name (RAVIEL 등 브랜드 제품) */}
          {product.id.startsWith('rvRV') && (
            <p className="text-[11px] font-black tracking-[0.3em] uppercase mb-1" style={{ color: '#C0504D' }}>RAVIEL</p>
          )}
          {/* Name ZH - 주 */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 leading-tight tracking-tight mb-1">
            {product.nameZh}
          </h1>
          {/* Name KO - 부 */}
          <p className="text-sm sm:text-base text-gray-400 font-medium mb-4 sm:mb-5 tracking-wide">{product.name}</p>

          {/* Specification */}
          <div className="mb-4 sm:mb-6 bg-gray-50 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-100">
            <p className="text-[13px] sm:text-[14px] font-semibold text-gray-700 tracking-wide">{product.specificationZh}</p>
            <p className="text-[12px] sm:text-[13px] text-gray-400 tracking-widest mt-0.5">{product.specification}</p>
          </div>

          {/* Price Box */}
          {!isLoggedIn ? (
            /* 비로그인: 가격 잠금 */
            <div className="rounded-2xl p-5 mb-4 sm:mb-6 flex flex-col items-center justify-center gap-3 text-center" style={{ backgroundColor: '#F7F4F0', border: '1.5px dashed #D8CEBF' }}>
              <div className="w-12 h-12 flex items-center justify-center rounded-full" style={{ backgroundColor: '#F0EAE0' }}>
                <i className="ri-lock-line text-2xl" style={{ color: '#8C7D6E' }}></i>
              </div>
              <div>
                <p className="text-[14px] font-black text-gray-800 mb-0.5">登录后可查看供货单价及箱价</p>
                <p className="text-[12px] tracking-widest" style={{ color: '#8C7D6E' }}>거래처 로그인 후 단가 확인 가능</p>
              </div>
              <button
                onClick={() => setLoginModalOpen(true)}
                className="px-6 py-2.5 rounded-xl text-[13px] font-black text-white transition-all hover:opacity-85 cursor-pointer whitespace-nowrap flex items-center gap-2"
                style={{ backgroundColor: '#2A2118' }}
              >
                <i className="ri-login-box-line"></i>
                合作伙伴登录 / 거래처 로그인
              </button>
            </div>
          ) : product.category === '섬유향수' ? (
            /* 섬유향수 전용 가격 박스 */
            <div className="rounded-2xl overflow-hidden mb-4 sm:mb-6" style={{ border: '1px solid #E2DDD8', backgroundColor: '#FAFAF8' }}>

              {/* 섹션1: MCN 참고 가격 (취소선) */}
              <div className="px-4 sm:px-5 pt-4 pb-3" style={{ borderBottom: '1px solid #EDE8E0' }}>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2.5" style={{ color: '#A09080' }}>
                  MCN参考价格 / MCN 참고가격
                </p>
                <div className="flex items-center gap-4 sm:gap-6">
                  <div>
                    <p className="text-[10px] sm:text-[11px] mb-0.5" style={{ color: '#B8A898' }}>消费者价格 / 소비자가격</p>
                    <p className="text-[14px] sm:text-[15px] font-semibold line-through" style={{ color: '#B8A898' }}>
                      1瓶 {product.consumerPrice} 元
                    </p>
                  </div>
                  <div className="w-px h-8 shrink-0" style={{ backgroundColor: '#E2DDD8' }}></div>
                  <div>
                    <p className="text-[10px] sm:text-[11px] mb-0.5" style={{ color: '#B8A898' }}>线上价格 / 온라인가격</p>
                    <p className="text-[14px] sm:text-[15px] font-semibold line-through" style={{ color: '#B8A898' }}>
                      1瓶 {product.onlinePrice} 元
                    </p>
                  </div>
                </div>
              </div>

              {/* 섹션2: B2B 파트너 핵심 정보 */}
              <div className="px-4 sm:px-5 pt-3.5 pb-4">
                {/* 섹션 헤더 */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-md" style={{ backgroundColor: '#E8F0EC' }}>
                    <i className="ri-shield-star-line text-[11px]" style={{ color: '#2E5240' }}></i>
                  </div>
                  <p className="text-[11px] font-bold tracking-wide" style={{ color: '#2E5240' }}>
                    B2B合作视角 <span className="font-normal" style={{ color: '#8C7D6E' }}>/ B2B 파트너십 보기</span>
                  </p>
                </div>

                {/* 라이브 특가 */}
                <div className="rounded-xl px-3.5 py-3 mb-3" style={{ backgroundColor: '#EEF4FF', border: '1px solid #C8D8F8' }}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <i className="ri-live-line text-[13px]" style={{ color: '#3B6FD4' }}></i>
                        <p className="text-[12px] sm:text-[13px] font-bold" style={{ color: '#3B6FD4' }}>直播特价</p>
                        <span className="text-[10px] font-semibold" style={{ color: '#6B9AE8' }}>/ 라이브 방송 특가</span>
                      </div>
                      <p className="text-[10px] leading-snug" style={{ color: '#6B9AE8' }}>
                        <i className="ri-mic-line mr-0.5"></i>
                        单价大幅降低！/ 단가 대폭 인하!
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[18px] sm:text-[20px] font-black leading-tight" style={{ color: '#2A52A8' }}>
                        1瓶 {product.livePrice1} 元
                      </p>
                      <p className="text-[14px] sm:text-[16px] font-bold" style={{ color: '#3B6FD4' }}>
                        3瓶 {product.livePrice3} 元
                      </p>
                    </div>
                  </div>
                </div>

                {/* 구분선 */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 h-px" style={{ backgroundColor: '#EDE8E0' }}></div>
                  <i className="ri-arrow-down-line text-[11px]" style={{ color: '#C8C0B8' }}></i>
                  <div className="flex-1 h-px" style={{ backgroundColor: '#EDE8E0' }}></div>
                </div>

                {/* 순수익 커미션 - 초강조 */}
                <div className="rounded-xl px-3.5 py-3.5 mb-3" style={{ backgroundColor: '#FFF7ED', border: '1.5px solid #F5C87A' }}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[11px] font-bold mb-0.5" style={{ color: '#92600A' }}>纯佣比例 / 순수익 커미션</p>
                      <p className="text-[10px] leading-snug" style={{ color: '#B8860B' }}>
                        利润空间大 / High Margin Guaranteed
                      </p>
                      {role === 'partner' && (
                        <p className="text-[10px] mt-0.5 font-semibold" style={{ color: '#C8860B' }}>
                          合作伙伴等级 (-5%) / 파트너 등급 적용
                        </p>
                      )}
                    </div>
                    <div className="flex items-end gap-1">
                      <span className="text-[42px] sm:text-[52px] font-black leading-none" style={{ color: '#E07B00' }}>
                        {getCommission(20, role)}
                      </span>
                      <div className="flex flex-col items-start pb-1">
                        <i className="ri-percent-line text-[22px] font-black" style={{ color: '#E07B00' }}></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 공급가격 안내 */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px]" style={{ color: '#A09080' }}>供货价格 / 공급 가격</p>
                    <p className="text-[10px]" style={{ color: '#B8A898' }}>详情咨询 / See details in inquiry</p>
                  </div>
                  <button
                    onClick={() => setModalOpen(true)}
                    className="text-[12px] sm:text-[13px] font-black px-4 py-2 rounded-xl whitespace-nowrap cursor-pointer transition-all hover:opacity-85 flex items-center gap-1.5"
                    style={{ color: '#fff', backgroundColor: '#2E7D52' }}
                  >
                    <i className="ri-customer-service-2-line text-sm"></i>
                    询价 / 문의하기
                  </button>
                </div>
              </div>
            </div>
          ) : product.category === '핸드크림' ? (
            /* 핸드크림 전용 B2B 가격 박스 */
            <div className="rounded-2xl overflow-hidden mb-4 sm:mb-6" style={{ border: '1px solid #E2DDD8', backgroundColor: '#FAFAF8' }}>

              {/* 소매가 취소선 */}
              <div className="px-4 sm:px-5 pt-4 pb-3 text-center" style={{ borderBottom: '1px solid #EDE8E0' }}>
                <p className="text-[11px] sm:text-[12px] line-through font-medium" style={{ color: '#B8A898' }}>
                  日常零售价: 1支 {product.hcRetailPrice}元 &nbsp;/&nbsp; 일상 소매가: {product.hcRetailPrice}元
                </p>
              </div>

              {/* B2B 파트너 핵심 정보 */}
              <div className="px-4 sm:px-5 pt-3.5 pb-4">
                {/* 섹션 헤더 */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-md" style={{ backgroundColor: '#E8F0EC' }}>
                    <i className="ri-shield-star-line text-[11px]" style={{ color: '#2E5240' }}></i>
                  </div>
                  <p className="text-[11px] font-bold tracking-wide" style={{ color: '#2E5240' }}>
                    B2B合作视角 <span className="font-normal" style={{ color: '#8C7D6E' }}>/ B2B 파트너십 보기</span>
                  </p>
                </div>

                {/* 라이브 특가 박스 */}
                <div className="rounded-xl px-3.5 py-3 mb-3" style={{ backgroundColor: '#EEF4FF', border: '1px solid #C8D8F8' }}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <i className="ri-mic-line text-[13px]" style={{ color: '#3B6FD4' }}></i>
                        <p className="text-[12px] sm:text-[13px] font-bold" style={{ color: '#3B6FD4' }}>直播间到手价</p>
                      </div>
                      <p className="text-[10px] leading-snug" style={{ color: '#6B9AE8' }}>
                        单价大幅降低！/ 단가 대폭 인하!
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[18px] sm:text-[20px] font-black leading-tight" style={{ color: '#2A52A8' }}>
                        1支 {product.hcLivePrice1}元
                      </p>
                      <p className="text-[14px] sm:text-[16px] font-bold" style={{ color: '#3B6FD4' }}>
                        3支 {product.hcLivePrice3}元
                      </p>
                    </div>
                  </div>
                </div>

                {/* 구분선 */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 h-px" style={{ backgroundColor: '#EDE8E0' }}></div>
                  <i className="ri-arrow-down-line text-[11px]" style={{ color: '#C8C0B8' }}></i>
                  <div className="flex-1 h-px" style={{ backgroundColor: '#EDE8E0' }}></div>
                </div>

                {/* 순수익 커미션 - 초강조 */}
                <div className="rounded-xl px-3.5 py-3.5 mb-3" style={{ backgroundColor: '#FFF7ED', border: '1.5px solid #F5C87A' }}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[11px] font-bold mb-0.5" style={{ color: '#92600A' }}>纯佣比例 / 순수익 커미션</p>
                      <p className="text-[10px] leading-snug" style={{ color: '#B8860B' }}>
                        利润空间大 / High Margin Guaranteed
                      </p>
                      {role === 'partner' && (
                        <p className="text-[10px] mt-0.5 font-semibold" style={{ color: '#C8860B' }}>
                          合作伙伴等级 (-5%) / 파트너 등급 적용
                        </p>
                      )}
                    </div>
                    <div className="flex items-end gap-1">
                      <span className="text-[42px] sm:text-[52px] font-black leading-none" style={{ color: '#E07B00' }}>
                        {getCommission(product.hcCommission ?? 25, role)}
                      </span>
                      <div className="flex flex-col items-start pb-1">
                        <i className="ri-percent-line text-[22px] font-black" style={{ color: '#E07B00' }}></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 공급가격 안내 + 문의 버튼 */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px]" style={{ color: '#A09080' }}>供货价格 / 공급 가격</p>
                    <p className="text-[10px]" style={{ color: '#B8A898' }}>详情咨询 / Details in inquiry</p>
                  </div>
                  <button
                    onClick={() => setModalOpen(true)}
                    className="text-[12px] sm:text-[13px] font-black px-4 py-2 rounded-xl whitespace-nowrap cursor-pointer transition-all hover:opacity-85 flex items-center gap-1.5"
                    style={{ color: '#fff', backgroundColor: '#2E7D52' }}
                  >
                    <i className="ri-customer-service-2-line text-sm"></i>
                    询价 / 문의하기
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* 일반 상품 가격 박스 */
            <div className="rounded-2xl p-4 sm:p-5 mb-4 sm:mb-6 space-y-2.5 sm:space-y-3" style={{ backgroundColor: '#F0EAE0', border: '1px solid #D8CEBF' }}>
              <div className="flex items-center justify-between pb-3 sm:pb-4" style={{ borderBottom: '1px solid #D8CEBF' }}>
                <div>
                  <p className="text-[13px] sm:text-[14px] font-bold tracking-wide" style={{ color: '#5C4F42' }}>箱价</p>
                  <p className="text-[11px] sm:text-[12px] tracking-widest mt-0.5" style={{ color: '#8C7D6E' }}>박스가격 (箱)</p>
                </div>
                {product.boxPrice > 0 ? (
                  <span className="text-3xl sm:text-4xl font-black tracking-tight" style={{ color: '#2A2118' }}>{applyMargin(product.boxPrice, role ?? 'admin')} <span className="text-xl sm:text-2xl">元</span></span>
                ) : (
                  <span className="text-[15px] sm:text-[18px] font-black tracking-tight px-3 py-1.5 rounded-xl whitespace-nowrap" style={{ color: '#2E5240', backgroundColor: '#D4E8DC' }}>询价 / 문의</span>
                )}
              </div>
              {product.retailPrice && product.retailPrice > 0 && (
                <div className="flex items-center justify-between py-1">
                  <div className="shrink-0 mr-3">
                    <p className="text-[12px] sm:text-[13px] tracking-wide" style={{ color: '#5C4F42' }}>零售价</p>
                    <p className="text-[10px] sm:text-[11px] tracking-widest" style={{ color: '#8C7D6E' }}>소비자가격</p>
                  </div>
                  <span className="text-[13px] sm:text-[15px] font-semibold tracking-tight text-right" style={{ color: '#5C4F42' }}>
                    ₩{product.retailPrice.toLocaleString()}
                    <span className="ml-1 text-[11px] sm:text-[12px]" style={{ color: '#8C7D6E' }}>≈ {Math.round(product.retailPrice / 216)}元</span>
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between py-1">
                <div className="shrink-0 mr-3">
                  <p className="text-[12px] sm:text-[13px] tracking-wide" style={{ color: '#5C4F42' }}>供货单价</p>
                  <p className="text-[10px] sm:text-[11px] tracking-widest" style={{ color: '#8C7D6E' }}>공급단가</p>
                </div>
                {product.unitPrice > 0 ? (
                  <span className="text-[15px] sm:text-[17px] font-bold tracking-tight whitespace-nowrap" style={{ color: '#C4985A' }}>{applyMargin(product.unitPrice, role ?? 'admin')} 元</span>
                ) : (
                  <span className="text-[13px] sm:text-[15px] font-bold tracking-tight" style={{ color: '#2E5240' }}>询价 / 문의</span>
                )}
              </div>
              {product.boxQuantity > 0 && (
                <div className="flex items-center justify-between py-1">
                  <div className="shrink-0 mr-3">
                    <p className="text-[12px] sm:text-[13px] tracking-wide" style={{ color: '#5C4F42' }}>箱入数</p>
                    <p className="text-[10px] sm:text-[11px] tracking-widest" style={{ color: '#8C7D6E' }}>박스입수</p>
                  </div>
                  <div className="text-right">
                    <span className="text-[14px] sm:text-[16px] font-semibold whitespace-nowrap block" style={{ color: '#5C4F42' }}>{product.boxQuantity}袋/箱</span>
                    <span className="text-[10px] sm:text-[11px] whitespace-nowrap" style={{ color: '#8C7D6E' }}>{product.boxQuantity}봉입</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Product Details */}
          <div className="mb-6 sm:mb-8">
            <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.25em] mb-3 sm:mb-4">
              商品信息 / 상품 정보
            </h3>
            {[
              {
                icon: 'ri-barcode-line',
                labelZh: '条形码',
                labelKo: '바코드',
                value: product.barcode || '-',
                valueZh: undefined as string | undefined,
                valueStyle: 'font-mono tracking-widest',
              },
              {
                icon: 'ri-stack-line',
                labelZh: '规格',
                labelKo: '규격',
                value: product.specificationZh,
                valueZh: product.specification,
                valueStyle: 'tracking-wide',
              },
              {
                icon: 'ri-time-line',
                labelZh: '保质期',
                labelKo: '유통기한',
                value: product.expiryInfoZh,
                valueZh: product.expiryInfo,
                valueStyle: 'tracking-wide',
              },
              {
                icon: 'ri-temp-cold-line',
                labelZh: '保存方式',
                labelKo: '보관방법',
                value: product.storageMethodZh,
                valueZh: product.storageMethod,
                valueStyle: 'tracking-wide',
              },
              {
                icon: 'ri-map-pin-2-line',
                labelZh: '产地',
                labelKo: '원산지',
                value: product.originZh,
                valueZh: product.origin,
                valueStyle: 'tracking-wide',
              },
            ].map(item => (
              <div key={item.labelZh} className="flex items-start gap-2.5 sm:gap-3 py-3 sm:py-3.5 border-b border-gray-100 last:border-0">
                <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-gray-100 rounded-lg shrink-0 mt-0.5">
                  <i className={`${item.icon} text-gray-500 text-xs sm:text-sm`}></i>
                </div>
                <div className="flex items-start justify-between flex-1 min-w-0 gap-2">
                  <div className="shrink-0 w-14 sm:w-16">
                    <p className="text-[12px] sm:text-[13px] font-semibold text-gray-700 leading-tight">{item.labelZh}</p>
                    <p className="text-[10px] sm:text-[11px] text-gray-400 leading-tight tracking-widest mt-0.5">{item.labelKo}</p>
                  </div>
                  <div className="text-right flex-1 min-w-0">
                    <p className={`text-[12px] sm:text-[13px] font-medium text-gray-800 leading-tight break-keep ${item.valueStyle || ''}`}>{item.value}</p>
                    {'valueZh' in item && item.valueZh && (
                      <p className="text-[10px] sm:text-[11px] text-gray-400 leading-tight tracking-widest mt-0.5 break-keep">{item.valueZh}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* B2B Info Box */}
          <div className="bg-black rounded-2xl p-4 sm:p-5 mb-4 sm:mb-6 text-white">
            <div className="flex items-start gap-2.5 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-white/10 rounded-xl shrink-0">
                <i className="ri-shield-check-line text-white text-sm sm:text-base"></i>
              </div>
              <div className="min-w-0">
                <p className="font-black text-[13px] sm:text-[14px] mb-1.5 sm:mb-2 tracking-wide">B2B交易须知</p>
                <p className="text-[11px] sm:text-[12px] text-gray-300 leading-relaxed tracking-wide">
                  最低订购1箱 · 开具税务发票 · 全国次日达
                </p>
                <p className="text-[10px] sm:text-[11px] text-gray-500 leading-relaxed tracking-widest mt-0.5">
                  최소 주문 1박스 이상 · 세금계산서 발행 · 전국 익일 배송
                </p>
                <p className="text-[11px] sm:text-[12px] text-gray-300 leading-relaxed tracking-wide mt-1">
                  大量订购可另行协商单价
                </p>
                <p className="text-[10px] sm:text-[11px] text-gray-500 leading-relaxed tracking-widest">
                  대량 주문 시 공급단가 별도 협의 가능
                </p>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setModalOpen(true)}
              className="flex-1 py-3.5 sm:py-4 bg-black text-white font-black text-[14px] sm:text-[15px] rounded-xl hover:bg-gray-800 transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 tracking-wide"
            >
              <i className="ri-customer-service-2-line text-base sm:text-lg"></i>
              询价 <span className="font-normal opacity-70 text-[12px] sm:text-[13px]">/ 문의하기</span>
            </button>
            <a
              href="#inquiry"
              onClick={(e) => {
                e.preventDefault();
                window.history.back();
                setTimeout(() => {
                  const el = document.getElementById('inquiry');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }, 300);
              }}
              className="px-5 sm:px-6 py-3.5 sm:py-4 border-2 border-gray-200 text-gray-700 font-semibold text-[13px] sm:text-[14px] rounded-xl hover:border-black hover:text-black transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 tracking-wide"
            >
              <i className="ri-phone-line"></i>
              电话咨询 <span className="font-normal opacity-60 text-[11px] sm:text-[12px]">/ 전화 상담</span>
            </a>
          </div>
        </div>
      </div>

      {/* Product Description & Key Features */}
      {product.description && (
        <div className="mt-10 sm:mt-14 border-t border-gray-100 pt-8 sm:pt-12">
          <div className="text-center mb-8 sm:mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.3em] uppercase px-4 py-1.5 rounded-full mb-4" style={{ backgroundColor: '#F0EAE0', color: '#8C7D6E' }}>
              Product Detail
            </span>
            <h2 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">商品详细信息</h2>
            <p className="text-sm text-gray-400 tracking-widest mt-1">상품 상세 정보</p>
          </div>

          {/* Description */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-10">
            <div className="rounded-2xl p-5 sm:p-7" style={{ backgroundColor: '#F7F4F0', border: '1px solid #EDE8E0' }}>
              <p className="text-[11px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: '#8C7D6E' }}>中文</p>
              <p className="text-[13px] sm:text-[14px] leading-relaxed" style={{ color: '#5C4F42' }}>{product.descriptionZh}</p>
            </div>
            <div className="rounded-2xl p-5 sm:p-7" style={{ backgroundColor: '#F7F4F0', border: '1px solid #EDE8E0' }}>
              <p className="text-[11px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: '#8C7D6E' }}>한국어</p>
              <p className="text-[13px] sm:text-[14px] text-gray-700 leading-relaxed">{product.description}</p>
            </div>
          </div>

          {/* Key Features */}
          {product.keyFeatures && product.keyFeatures.length > 0 && (
            <div>
              <h3 className="text-[11px] font-bold tracking-[0.25em] uppercase text-center mb-5 sm:mb-6" style={{ color: '#8C7D6E' }}>
                主要特点 · Key Features
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                {product.keyFeatures.map((feat, idx) => (
                  <div key={idx} className="rounded-2xl p-4 sm:p-6 text-center" style={{ backgroundColor: '#FAFAF8', border: '1px solid #EDE8E0' }}>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-2xl mx-auto mb-3 sm:mb-4" style={{ backgroundColor: '#F0EAE0' }}>
                      <i className={`${feat.icon} text-lg sm:text-xl`} style={{ color: '#5C4F42' }}></i>
                    </div>
                    <p className="text-[12px] sm:text-[13px] font-bold text-gray-800 mb-0.5">{feat.titleZh}</p>
                    <p className="text-[10px] sm:text-[11px] mb-2 sm:mb-3" style={{ color: '#8C7D6E' }}>{feat.titleKo}</p>
                    <p className="text-[11px] sm:text-[12px] text-gray-500 leading-relaxed">{feat.descZh}</p>
                    <p className="text-[10px] sm:text-[11px] mt-1 leading-relaxed" style={{ color: '#A09080' }}>{feat.descKo}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Atopalm Brand Section */}
      {product.category === '스킨케어·뷰티' && <AtopalmBrandSection />}

      {/* Dressroom Brand Section */}
      {(product.category === '섬유향수' || product.category === '핸드크림') && <DressroomBrandSection />}

      {/* HBAF Brand Section */}
      {product.id.startsWith('hbHB') && <HBAFBrandSection />}

      {/* RAVIEL Brand Section */}
      {product.id.startsWith('rvRV') && <RavielBrandSection />}

      <InquiryModal product={modalOpen ? product : null} onClose={() => setModalOpen(false)} />
      <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </>
  );
}
